import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { IoMdArrowBack } from "react-icons/io";
import { FiPackage, FiInfo, FiSmartphone, FiMapPin, FiCamera, FiCheckCircle, FiChevronRight, FiSearch, FiX, FiAlertCircle } from "react-icons/fi";
import imageCompression from "browser-image-compression";
import * as api from "../../api/index";
import productData from "./productlist";

const lagosLGAs = [
  "Agege", "Ajeromi-Ifelodun", "Alimosho", "Amuwo-Odofin", "Apapa", "Badagry",
  "Epe", "Eti-Osa", "Ibeju-Lekki", "Ifako-Ijaiye", "Ikeja", "Ikorodu", "Kosofe",
  "Lagos Island", "Lagos Mainland", "Mushin", "Ojo", "Oshodi-Isolo", "Shomolu", "Surulere",
];

export default function SellProduct() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    phone: "",
    lga: "",
    category: "",
    subCategory: "",
    product: "",
    model: "",
    condition: "New",
    gallery: [],
  });

  const [galleryError, setGalleryError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const galleryRef = useRef(null);

  const token = localStorage.getItem("token");
  let userId = "", email = "", isAuthenticated = false;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      userId = decoded.id || decoded._id || "";
      email = decoded.email || "";
      isAuthenticated = true;
    } catch (err) { console.error("Invalid token:", err); }
  }

  // SEARCH LOGIC
  const allItems = [];
  productData.forEach((block) => {
    block.products.forEach((product) => {
      allItems.push({ type: "product", name: product.name, category: block.category, subcategory: block.subcategory });
      product.models.forEach((model) =>
        allItems.push({ type: "model", name: model, category: block.category, subcategory: block.subcategory, product: product.name })
      );
    });
  });

  const suggestions = allItems
    .filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 6);

  // DROPDOWN DATA
  const categoriesList = [...new Set(productData.map((item) => item.category))];
  const subcategories = productData.filter((item) => item.category === form.category).map((item) => item.subcategory);
  const productsList = productData.find((item) => item.category === form.category && item.subcategory === form.subCategory)?.products.map((p) => p.name) || [];
  const modelsList = productData.find((item) => item.category === form.category && item.subcategory === form.subCategory)?.products.find((p) => p.name === form.product)?.models || [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const selectFromSearch = (s) => {
    setForm({
      ...form,
      category: s.category,
      subCategory: s.subcategory,
      product: s.type === "model" ? s.product : s.name,
      model: s.type === "model" ? s.name : "",
    });
    setSearchTerm(s.name);
    setShowSuggestions(false);
  };

  const handleGalleryChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (form.gallery.length + selectedFiles.length > 5) { alert("Max 5 images"); return; }
    setLoading(true);
    try {
      const compressedImages = [];
      for (const file of selectedFiles) {
        const compressedFile = await imageCompression(file, { maxSizeMB: 0.6, maxWidthOrHeight: 1280 });
        compressedImages.push(compressedFile);
      }
      setForm((prev) => ({ ...prev, gallery: [...prev.gallery, ...compressedImages] }));
    } catch { setGalleryError("Upload failed"); } finally { setLoading(false); }
  };

  // ADDED: DELETE IMAGE FUNCTION
  const removeImage = (index) => {
    setForm((prev) => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) return;
    if (form.gallery.length < 3) {
      setGalleryError("Upload at least 3 images.");
      galleryRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => { if (key !== "gallery") formData.append(key, value); });
      formData.append("userId", userId);
      formData.append("email", email);
      form.gallery.forEach((file) => formData.append("images", file));
      const res = await api.uploadProduct(formData);
      if (res.data.success) navigate("/congratulation");
    } catch { setSubmitError("Upload error."); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col relative overflow-hidden">
      {/* Header */}
      <div className="bg-white px-6 py-5 flex items-center shadow-sm border-b border-gray-100 z-20">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full transition"><IoMdArrowBack size={24} /></button>
        <div className="ml-4">
          <h1 className="text-[10px] font-black uppercase tracking-widest text-orange-600">Post Item</h1>
          <p className="text-lg font-black text-gray-900 tracking-tighter">Marketplace</p>
        </div>
      </div>

      <div className="flex-1 p-4 max-w-2xl mx-auto w-full z-10 pt-4 pb-20">
        
        {/* Simple Welcome Banner */}
        {!isAuthenticated && (
          <div className="mb-6 bg-white border border-orange-100 p-4 rounded-2xl flex items-start gap-3 shadow-sm">
            <FiAlertCircle className="text-orange-500 mt-1 flex-shrink-0" size={18} />
            <div>
              <p className="text-[13px] font-bold text-gray-800 leading-tight">Welcome User! You need an account to post.</p>
              <p className="text-[11px] text-gray-500">Create a buyer or seller account in minutes to start selling.</p>
              <button onClick={() => navigate("/auth")} className="text-[11px] font-black text-orange-600 mt-1 uppercase tracking-wider">Join Now →</button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-[2.5rem] p-6 md:p-8 shadow-xl shadow-gray-200/40 border border-gray-50 space-y-8">
          
          <div className="space-y-4">
            {/* PRODUCT NAME INPUT */}
            <input 
              type="text" 
              name="name" 
              value={form.name} 
              onChange={handleInputChange} 
              placeholder="Product Name (e.g. iPhone 6)" 
              className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold outline-none border border-transparent focus:border-orange-500" 
              required 
            />

            {/* DESCRIPTION */}
            <textarea 
              name="description" 
              value={form.description} 
              onChange={handleInputChange} 
              placeholder="Tell us about the item..." 
              className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold outline-none min-h-[100px] border border-transparent focus:border-orange-500" 
              required 
            />
            
            <div className="grid grid-cols-2 gap-4">
              <input type="number" name="price" value={form.price} onChange={handleInputChange} placeholder="Price (₦)" className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold outline-none border border-transparent focus:border-orange-500" required />
              <input type="tel" name="phone" value={form.phone} onChange={handleInputChange} placeholder="Phone Number" className="w-full px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold outline-none border border-transparent focus:border-orange-500" required />
            </div>
          </div>

          {/* CATEGORIZATION SECTION */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <FiSearch className="text-orange-500" size={14} />
              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">Find category faster</label>
            </div>

            {/* SEARCH BAR (TOP OF SELECTS) */}
            <div className="relative">
              <input 
                type="text" 
                placeholder="Quick search category..." 
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setShowSuggestions(true); }}
                className="w-full pl-12 pr-4 py-4 bg-gray-900 text-white rounded-2xl text-sm font-bold outline-none" 
              />
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-orange-500" size={20} />
              
              {showSuggestions && searchTerm && (
                <div className="absolute top-full left-0 w-full bg-white border border-gray-100 rounded-2xl shadow-2xl mt-2 max-h-60 overflow-y-auto z-50 p-2">
                  {suggestions.map((s, i) => (
                    <div key={i} onClick={() => selectFromSearch(s)} className="p-3 hover:bg-orange-50 rounded-xl cursor-pointer transition">
                      <p className="text-sm font-black text-gray-900">{s.name}</p>
                      <p className="text-[9px] font-bold text-gray-400 uppercase">{s.subcategory} • {s.category}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* MANUAL SELECTS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <select name="category" value={form.category} onChange={handleInputChange} className="px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold outline-none border border-gray-100">
                <option value="">Select Category</option>
                {categoriesList.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              <select name="subCategory" value={form.subCategory} onChange={handleInputChange} disabled={!form.category} className="px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold outline-none border border-gray-100 disabled:opacity-50">
                <option value="">Select Sub-Category</option>
                {subcategories.map(s => <option key={s} value={s}>{s}</option>)}
              </select>

              <select name="product" value={form.product} onChange={handleInputChange} disabled={!form.subCategory} className="px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold outline-none border border-gray-100 disabled:opacity-50">
                <option value="">Select Brand</option>
                {productsList.map(p => <option key={p} value={p}>{p}</option>)}
              </select>

              <select name="model" value={form.model} onChange={handleInputChange} disabled={modelsList.length === 0} className="px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold outline-none border border-gray-100 disabled:opacity-50">
                <option value="">Select Model</option>
                {modelsList.map(m => <option key={m} value={m}>{m}</option>)}
              </select>

              <select name="lga" value={form.lga} onChange={handleInputChange} className="px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold outline-none border border-gray-100">
                <option value="">Select LGA</option>
                {lagosLGAs.map(l => <option key={l} value={l}>{l}</option>)}
              </select>

              <select name="condition" value={form.condition} onChange={handleInputChange} className="px-5 py-4 bg-gray-50 rounded-2xl text-sm font-bold outline-none border border-gray-100">
                <option value="New">Brand New</option>
                <option value="Used">Used</option>
              </select>
            </div>
          </div>

          {/* Photos */}
          <div ref={galleryRef} className="space-y-4">
            <h3 className="text-[11px] font-black uppercase text-gray-400">Photos ({form.gallery.length}/5)</h3>
            <div className="grid grid-cols-4 gap-3">
              {form.gallery.map((file, i) => (
                <div key={i} className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden group">
                  <img src={URL.createObjectURL(file)} className="w-full h-full object-cover" alt="preview" />
                  {/* CANCEL BUTTON */}
                  <button 
                    type="button" 
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full hover:bg-red-500 transition-colors"
                  >
                    <FiX size={14} />
                  </button>
                </div>
              ))}
              {form.gallery.length < 5 && (
                <label className="aspect-square border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50">
                  <FiCamera size={20} className="text-gray-400" />
                  <input type="file" multiple accept="image/*" onChange={handleGalleryChange} className="hidden" />
                </label>
              )}
            </div>
            {galleryError && <p className="text-red-500 text-[10px] font-bold">{galleryError}</p>}
          </div>

          <button 
            type="submit" 
            disabled={loading || !isAuthenticated} 
            className={`w-full py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg transition-all ${!isAuthenticated ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-black active:scale-95'}`}
          >
            {!isAuthenticated ? "Login to Post" : loading ? "Uploading..." : "Post Item Now"}
          </button>
        </form>
      </div>
    </div>
  );
}